"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChipProps extends Omit<React.ComponentProps<"span">, "prefix"> {
  /** Muted prefix shown before the value (e.g. the filter field name). */
  prefix?: React.ReactNode;
  /** When provided, renders a remove button. */
  onRemove?: () => void;
  removeLabel?: string;
  tone?: "accent" | "neutral";
}

/**
 * Removable chip used for active filters and selected values.
 */
function Chip({
  className,
  prefix,
  onRemove,
  removeLabel,
  tone = "accent",
  children,
  ...props
}: ChipProps) {
  return (
    <span
      data-slot="chip"
      className={cn(
        "inline-flex h-7 max-w-full items-center gap-1 rounded-full border pl-2.5 text-[12.5px] leading-none",
        "animate-fade-in",
        tone === "accent"
          ? "border-[rgb(167_139_250/0.3)] bg-accent-tint text-ink"
          : "border-line bg-surface-2 text-ink-2",
        onRemove ? "pr-1" : "pr-2.5",
        className
      )}
      {...props}
    >
      {prefix && <span className="shrink-0 text-ink-3">{prefix}</span>}
      <span className="truncate">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ?? "Remove"}
          className="ml-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-[rgb(255_255_255/0.12)] hover:text-ink focus-visible:outline-2 focus-visible:outline-accent"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

export { Chip };
